import { useEffect, useState } from "react";
import { Megaphone } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { api } from "@/lib/api";
import { Panel, PrimaryButton, Title, ago } from "@/components/admin/kit";

export function AnnouncePanel() {
  const [now, setNow] = useState(undefined);
  const [form, setForm] = useState({ text: "", link: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => { api("/admin/announce").then((d) => setNow(d.announce)).catch((e) => toast.error(e.message)); }, []);
  const run = async (opts, done) => {
    setBusy(true);
    try {
      setNow((await api("/admin/announce", opts)).announce);
      toast.success(done);
      return true;
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const push = async (e) => {
    e.preventDefault();
    if (await run({ method: "PUT", body: form }, "Sent. Everyone in town sees it within 30 seconds.")) setForm({ text: "", link: "" });
  };
  return (
    <Panel className="max-w-xl" testId="announce-panel">
      <Title hint="Pops up once for everyone in town, and for anyone who comes in later, until you replace or take it down.">Announcement</Title>
      {now && (
        <div className="mb-4 flex items-start gap-3 rounded-xl bg-amber-50 px-3 py-2.5 text-sm" data-testid="announce-current">
          <Megaphone size={16} className="mt-0.5 shrink-0 text-amber-700" />
          <div className="min-w-0 flex-1">
            <p className="whitespace-pre-line font-semibold text-amber-900">{now.text}</p>
            <p className="text-xs text-amber-700">{now.link && <>{now.link} · </>}sent {ago(now.at)}</p>
          </div>
          <PrimaryButton tone="danger" disabled={busy} onClick={() => run({ method: "DELETE" }, "Taken down")} testId="announce-clear">Take down</PrimaryButton>
        </div>
      )}
      <form onSubmit={push} className="space-y-2.5">
        <Textarea value={form.text} maxLength={200} rows={3} placeholder="What should everyone see?" onChange={(e) => setForm({ ...form, text: e.target.value })} data-testid="announce-text" />
        <Input value={form.link} maxLength={500} placeholder="Link (optional), e.g. https://x.com/…" onChange={(e) => setForm({ ...form, link: e.target.value })} data-testid="announce-link" />
        <div className="flex items-center justify-between">
          <span className="text-xs tabular-nums text-slate-400">{form.text.length}/200</span>
          <PrimaryButton type="submit" disabled={busy || !form.text.trim()} testId="announce-send">{now ? "Replace and send" : "Send to everyone"}</PrimaryButton>
        </div>
      </form>
    </Panel>
  );
}
